import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Button, ButtonVariant } from "../Buttons/button.js";
import { Size } from "../helpers/helpers.js";
import { Loader, LoaderStyle } from "../Loader/loader.js";

export interface FilterBarProps {
  /**
   * Filter controls shown in the bar. Keep common filters visible even when
   * they are set to a neutral "All" value, and add less common filters as
   * needed for the current surface. Omit when the bar only hosts leading or
   * trailing controls (e.g. a search input with a period selector).
   */
  items?: ReactNode;
  /** Contextual controls between the add-filter button and the filter items (e.g. a playbook switcher or search input). */
  leadingItems?: ReactNode;
  /** Filter-like controls appended after the filter items (e.g. a page-specific period selector). */
  trailingItems?: ReactNode;
  /** Rendered as the first control in the bar. */
  addFilterButton?: ReactNode;
  resultSummary?: ReactNode;
  isLoading?: boolean;
  onReset?: () => void;
  resetLabel?: string;
  rightActions?: ReactNode;
}

export const FilterBar: FC<FilterBarProps> = ({
  items,
  leadingItems,
  trailingItems,
  addFilterButton,
  resultSummary,
  isLoading = false,
  onReset,
  resetLabel = "Reset",
  rightActions,
}) => {
  const statusContent = isLoading ? (
    <Loader className="m-0 block" style={LoaderStyle.SPINNER} />
  ) : (
    <>
      {resultSummary ? (
        <div className="truncate text-xs font-normal text-muted-foreground">
          {resultSummary}
        </div>
      ) : null}

      {onReset ? (
        <Button
          variant={ButtonVariant.Tertiary}
          size={Size.SMALL}
          onClick={onReset}
        >
          {resetLabel}
        </Button>
      ) : null}
    </>
  );

  return (
    <div className="relative -mb-2 min-w-0 bg-card px-4 pt-3 pb-2 sm:-mb-3 sm:px-6 sm:pt-4 sm:pb-3">
      <div
        className={twMerge(
          "flex min-h-9 min-w-0 items-start justify-between gap-4 rounded-xl bg-gray-50 py-1 pl-1",
          rightActions ? "pr-1" : "pr-4",
        )}
      >
        {/* All controls are direct flex children so each one shrinks toward
            its own min width (truncating its label) and never overlaps a
            neighbor; anything past the row's edge is clipped. */}
        <div className="flex min-w-0 flex-1 items-start gap-1 overflow-hidden">
          {addFilterButton ? (
            <div data-filter-bar-add-filter className="shrink-0">
              {addFilterButton}
            </div>
          ) : null}
          {leadingItems}
          {items}
          {trailingItems}
        </div>

        {isLoading || resultSummary || onReset || rightActions ? (
          <div
            data-filter-bar-status
            className="flex min-h-7 shrink-0 items-center gap-2"
          >
            {statusContent}
            {rightActions}
          </div>
        ) : null}
      </div>

      <div
        aria-hidden
        data-filter-bar-fade
        className="pointer-events-none absolute inset-x-0 -bottom-2 h-2 bg-linear-to-b from-card to-transparent sm:-bottom-3 sm:h-3"
      />
    </div>
  );
};
